import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/**
 * Iter XX.44.G — Partner Profile Editor.
 * Owner-side editor for every field surfaced on /@username, including
 * the new enhanced fields (tagline, achievements, testimonials,
 * gallery, pricing tiers, booking / website / map).
 *
 * Layout intentionally utilitarian — this is a workhorse form, not a
 * marketing surface. Keeps the code lean & typed.
 */
export default function PartnerEdit() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/login?return_to=/partner/edit"); return; }
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/partners/me");
        if (!alive) return;
        if (!r.data.partner) { navigate("/partner/claim"); return; }
        // Fill defaults so form controls stay controlled.
        const p = r.data.partner;
        setPartner({
          ...p,
          tagline: p.tagline || "",
          bio: p.bio || "",
          booking_url: p.booking_url || "",
          website_url: p.website_url || "",
          map_query: p.map_query || "",
          achievements: p.achievements || [],
          testimonials: p.testimonials || [],
          gallery: p.gallery || [],
          pricing_tiers: p.pricing_tiers || [],
        });
        setLoading(false);
      } catch {
        if (alive) { toast.error("Couldn't load your profile"); setLoading(false); }
      }
    })();
    return () => { alive = false; };
  }, [authLoading, user, navigate]);

  function setField(k, v) { setPartner(p => ({ ...p, [k]: v })); }

  function addRow(k, row) {
    setPartner(p => ({ ...p, [k]: [...(p[k] || []), row] }));
  }
  function updateRow(k, i, patch) {
    setPartner(p => ({
      ...p,
      [k]: (p[k] || []).map((r, idx) => idx === i ? { ...r, ...patch } : r),
    }));
  }
  function removeRow(k, i) {
    setPartner(p => ({ ...p, [k]: (p[k] || []).filter((_, idx) => idx !== i) }));
  }

  async function handleSave() {
    if (!partner) return;
    setSaving(true);
    try {
      const payload = {
        display_name: partner.display_name,
        bio: partner.bio || null,
        tagline: partner.tagline || null,
        city: partner.city || null,
        country: partner.country || null,
        booking_url: partner.booking_url || null,
        website_url: partner.website_url || null,
        map_query: partner.map_query || null,
        achievements: partner.achievements || [],
        testimonials: partner.testimonials || [],
        gallery: partner.gallery || [],
        pricing_tiers: partner.pricing_tiers || [],
      };
      await api.patch("/partners/me", payload);
      toast.success("Profile saved");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Save failed";
      toast.error(String(msg));
    } finally {
      setSaving(false);
    }
  }

  if (authLoading || loading) {
    return <div className="min-h-screen bg-neutral-950 text-neutral-100 flex items-center justify-center">Loading…</div>;
  }
  if (!partner) return null;

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100" data-testid="partner-edit-page">
      <div className="max-w-3xl mx-auto px-6 py-10">
        <div className="mb-8">
          <Link to="/partner" className="text-sm text-neutral-500 hover:text-neutral-300" data-testid="partner-edit-back">← Back to dashboard</Link>
          <h1 className="text-3xl font-semibold mt-2">Edit your Gully page</h1>
          <p className="text-sm text-neutral-400 mt-1">
            Everything here is public. Never add anything you wouldn't put on your own website.
          </p>
        </div>

        <Section title="Basics">
          <Field label="Display name">
            <input value={partner.display_name || ""} onChange={e => setField("display_name", e.target.value)} className={inputClass} data-testid="pe-display-name" maxLength={80} />
          </Field>
          <Field label="Tagline (one line, shown under your name)">
            <input value={partner.tagline || ""} onChange={e => setField("tagline", e.target.value)} className={inputClass} data-testid="pe-tagline" maxLength={120} />
          </Field>
          <Field label="Bio (up to 600 chars)">
            <textarea value={partner.bio || ""} onChange={e => setField("bio", e.target.value)} className={inputClass + " h-24"} data-testid="pe-bio" maxLength={600} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="City"><input value={partner.city || ""} onChange={e => setField("city", e.target.value)} className={inputClass} data-testid="pe-city" /></Field>
            <Field label="Country"><input value={partner.country || ""} onChange={e => setField("country", e.target.value)} className={inputClass} data-testid="pe-country" /></Field>
          </div>
        </Section>

        <Section title="Contact & links">
          <Field label="Booking URL (Calendly, WhatsApp deep-link, form, etc.)">
            <input value={partner.booking_url || ""} onChange={e => setField("booking_url", e.target.value)} className={inputClass} placeholder="https://cal.com/you" data-testid="pe-booking-url" />
          </Field>
          <Field label="Academy website URL">
            <input value={partner.website_url || ""} onChange={e => setField("website_url", e.target.value)} className={inputClass} placeholder="https://youracademy.com" data-testid="pe-website-url" />
          </Field>
          <Field label="Map query (city / venue / address to embed a Google Map)">
            <input value={partner.map_query || ""} onChange={e => setField("map_query", e.target.value)} className={inputClass} placeholder="e.g. Dilip Vengsarkar Academy Mumbai" maxLength={200} data-testid="pe-map-query" />
          </Field>
        </Section>

        <Section title="Achievements" testid="pe-achievements" onAdd={() => addRow("achievements", { title: "", subtitle: "", year: "" })} addTestid="pe-add-achievement">
          {partner.achievements.map((a, i) => (
            <div key={i} className={rowClass} data-testid={`pe-achievement-${i}`}>
              <input value={a.title || ""} onChange={e => updateRow("achievements", i, { title: e.target.value })} placeholder="Title (e.g. Ranji U-19 selection)" className={inputClass} data-testid={`pe-achievement-title-${i}`} />
              <input value={a.subtitle || ""} onChange={e => updateRow("achievements", i, { subtitle: e.target.value })} placeholder="Subtitle" className={inputClass} data-testid={`pe-achievement-subtitle-${i}`} />
              <div className="flex gap-2">
                <input value={a.year || ""} onChange={e => updateRow("achievements", i, { year: e.target.value })} placeholder="Year" className={inputClass + " w-24"} data-testid={`pe-achievement-year-${i}`} />
                <Button variant="outline" size="sm" onClick={() => removeRow("achievements", i)} className="border-red-800/60 text-red-400" data-testid={`pe-remove-achievement-${i}`}>Remove</Button>
              </div>
            </div>
          ))}
        </Section>

        <Section title="Testimonials" testid="pe-testimonials" onAdd={() => addRow("testimonials", { quote: "", attribution: "", role: "" })} addTestid="pe-add-testimonial">
          {partner.testimonials.map((t, i) => (
            <div key={i} className={rowClass} data-testid={`pe-testimonial-${i}`}>
              <textarea value={t.quote || ""} onChange={e => updateRow("testimonials", i, { quote: e.target.value })} placeholder="Quote" className={inputClass + " h-20"} data-testid={`pe-testimonial-quote-${i}`} maxLength={400} />
              <div className="grid grid-cols-2 gap-2">
                <input value={t.attribution || ""} onChange={e => updateRow("testimonials", i, { attribution: e.target.value })} placeholder="Attribution (name)" className={inputClass} data-testid={`pe-testimonial-attr-${i}`} />
                <input value={t.role || ""} onChange={e => updateRow("testimonials", i, { role: e.target.value })} placeholder="Role (parent / player / coach)" className={inputClass} data-testid={`pe-testimonial-role-${i}`} />
              </div>
              <Button variant="outline" size="sm" onClick={() => removeRow("testimonials", i)} className="border-red-800/60 text-red-400 self-end" data-testid={`pe-remove-testimonial-${i}`}>Remove</Button>
            </div>
          ))}
        </Section>

        <Section title="Gallery (image URLs)" testid="pe-gallery" onAdd={() => addRow("gallery", { url: "", caption: "" })} addTestid="pe-add-gallery">
          {partner.gallery.map((g, i) => (
            <div key={i} className={rowClass} data-testid={`pe-gallery-${i}`}>
              <input value={g.url || ""} onChange={e => updateRow("gallery", i, { url: e.target.value })} placeholder="Image URL (https://…)" className={inputClass} data-testid={`pe-gallery-url-${i}`} />
              <div className="flex gap-2">
                <input value={g.caption || ""} onChange={e => updateRow("gallery", i, { caption: e.target.value })} placeholder="Caption (optional)" className={inputClass} data-testid={`pe-gallery-caption-${i}`} maxLength={120} />
                <Button variant="outline" size="sm" onClick={() => removeRow("gallery", i)} className="border-red-800/60 text-red-400" data-testid={`pe-remove-gallery-${i}`}>Remove</Button>
              </div>
            </div>
          ))}
        </Section>

        <Section title="Sessions & pricing" testid="pe-pricing" onAdd={() => addRow("pricing_tiers", { name: "", price_display: "", description: "", cta_label: "", cta_url: "" })} addTestid="pe-add-pricing">
          {partner.pricing_tiers.map((t, i) => (
            <div key={i} className={rowClass} data-testid={`pe-pricing-${i}`}>
              <div className="grid grid-cols-2 gap-2">
                <input value={t.name || ""} onChange={e => updateRow("pricing_tiers", i, { name: e.target.value })} placeholder="Tier name (e.g. 1:1 coaching)" className={inputClass} data-testid={`pe-pricing-name-${i}`} />
                <input value={t.price_display || ""} onChange={e => updateRow("pricing_tiers", i, { price_display: e.target.value })} placeholder="Price (e.g. ₹1,500 / hr)" className={inputClass} data-testid={`pe-pricing-price-${i}`} />
              </div>
              <textarea value={t.description || ""} onChange={e => updateRow("pricing_tiers", i, { description: e.target.value })} placeholder="What's included" className={inputClass + " h-16"} data-testid={`pe-pricing-desc-${i}`} maxLength={400} />
              <div className="grid grid-cols-3 gap-2">
                <input value={t.cta_label || ""} onChange={e => updateRow("pricing_tiers", i, { cta_label: e.target.value })} placeholder="CTA label (Book / Enquire)" className={inputClass} data-testid={`pe-pricing-cta-label-${i}`} />
                <input value={t.cta_url || ""} onChange={e => updateRow("pricing_tiers", i, { cta_url: e.target.value })} placeholder="CTA URL (falls back to booking)" className={inputClass + " col-span-2"} data-testid={`pe-pricing-cta-url-${i}`} />
              </div>
              <Button variant="outline" size="sm" onClick={() => removeRow("pricing_tiers", i)} className="border-red-800/60 text-red-400 self-end" data-testid={`pe-remove-pricing-${i}`}>Remove</Button>
            </div>
          ))}
        </Section>

        <div className="sticky bottom-4 bg-neutral-950/90 backdrop-blur border border-neutral-800 rounded-lg p-3 flex items-center justify-between mt-8">
          <div className="text-xs text-neutral-500">Changes appear on your public page immediately after saving.</div>
          <Button onClick={handleSave} disabled={saving} className="bg-emerald-500 hover:bg-emerald-400 text-neutral-950" data-testid="pe-save-btn">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>
    </div>
  );
}

const inputClass = "w-full bg-neutral-950 border border-neutral-800 rounded px-3 py-2 text-sm text-neutral-100 focus:border-emerald-500 focus:outline-none";
const rowClass = "flex flex-col gap-2 bg-neutral-900/60 border border-neutral-800 rounded-lg p-3";

function Section({ title, children, onAdd, addTestid, testid }) {
  return (
    <section className="mb-8" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm uppercase tracking-wider text-neutral-500">{title}</h2>
        {onAdd && (
          <Button variant="outline" size="sm" onClick={onAdd} className="border-neutral-700" data-testid={addTestid}>+ Add</Button>
        )}
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block mb-3">
      <span className="block text-xs text-neutral-500 mb-1">{label}</span>
      {children}
    </label>
  );
}
